import { inject, Injectable } from '@angular/core';

import { StorageAdapter, StorageType } from '../../services';
import { ConfigSettingsService } from '../config-settings';
import { CryptoProvider } from '../crypto/crypto';

@Injectable({
  providedIn: 'root',
})
export class PinService {
  private readonly cryptoService = inject(CryptoProvider);
  private readonly configSettingsService = inject(ConfigSettingsService);
  private readonly storageAdapter = inject(StorageAdapter);

  private readonly persistPin = this.configSettingsService.appSettings.pinConfiguration?.storage === 'persistent';
  private readonly storage = this.storageAdapter.get(this.persistPin ? StorageType.PERSISTENT : StorageType.MEMORY);

  /*
   * Sets the PIN for the given username, encrypting it with the provided userSecret.
   *
   * @param username - The username for which to set the PIN.
   * @param userSecret - The secret used to encrypt the PIN.
   * @param pin - The PIN to be set.
   * */
  public async setPin(username: string, userSecret: string, pin: string): Promise<void> {
    const base64Username = window.btoa(username);
    const encryptedPin = await this.cryptoService.encryptToHex(userSecret, pin);
    await this.storage.setValue(base64Username, encryptedPin);
  }

  /*
   * Retrieves and decrypts the PIN for the given username using the provided userSecret.
   *
   * @param username - The username for which to retrieve the PIN.
   * @param userSecret - The secret used to decrypt the PIN.
   * @returns The decrypted PIN as a string, or null if not found.
   * */
  public async getPin(username: string, userSecret: string): Promise<string | null> {
    await this.migrateOldPin(username, userSecret);

    const base64Username = window.btoa(username);
    const encryptedPin = await this.storage.getValue<string>(base64Username);
    if (!encryptedPin) {
      return null;
    }
    return this.cryptoService.decryptToHexString(userSecret, encryptedPin);
  }

  /* * Resets (removes) the PIN for the given username.
   *
   * @param username - The username for which to reset the PIN.
   * */
  public async resetPin(username: string): Promise<void> {
    const base64Username = window.btoa(username);
    await this.storage.removeValue(base64Username);
  }

  /*
   * Migrates an old PIN stored under a generic key to a new key based on the username.
   * */
  private async migrateOldPin(username: string, userSecret: string): Promise<void> {
    if (!this.persistPin) {
      return;
    }

    const oldPinKey = 'pin';

    const encryptedOldPin = await this.storage.getValue<string>(oldPinKey);

    if (!encryptedOldPin) {
      return;
    }

    const decryptedOldPin = await this.cryptoService.decryptToHexString(userSecret, encryptedOldPin);

    if (decryptedOldPin !== null) {
      await this.setPin(username, userSecret, decryptedOldPin);
    }

    await this.storage.removeValue(oldPinKey);
  }
}
